import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";

import commoncss from "../../css/common.module.css";
import libcss from "../../css/library.module.css";


import closeIcon from "../../assets/icons/custModeLCloseIcon.svg";

/**
 * Props:
 *  value: { tableTitle, tableRow:[{name,lineType}], tableColumn:[{name,lineType}] }
 *  onChange(nextValue)
 *  forceOpen?: boolean
 *  onAfterOpen?: () => void
 *  disabled?: boolean
 */

const DragHandleIcon = ({ disabled }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      cursor: disabled ? "default" : "grab",
      opacity: disabled ? 0.3 : 0.7,
    }}
  >
    <circle cx="9" cy="5" r="1.5" fill="currentColor" />
    <circle cx="15" cy="5" r="1.5" fill="currentColor" />
    <circle cx="9" cy="12" r="1.5" fill="currentColor" />
    <circle cx="15" cy="12" r="1.5" fill="currentColor" />
    <circle cx="9" cy="19" r="1.5" fill="currentColor" />
    <circle cx="15" cy="19" r="1.5" fill="currentColor" />
  </svg>
);
export default function TemplateTableGenerator({
  value,
  onChange,
  forceOpen = false,
  onAfterOpen,
  disabled = false,
  allowReorder = false,
}) {
  const totalRows = 20;
  const totalCols = 20;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [selectedRows, setSelectedRows] = useState(0);
  const [selectedCols, setSelectedCols] = useState(0);
  const [tableName, setTableName] = useState(value?.tableTitle || "");
  const [generated, setGenerated] = useState(false);
  const [cellValues, setCellValues] = useState({});

  const modalPrefilledRef = useRef(false);
  const skipPrefillOnCloseRef = useRef(false);
  const lastSentPayloadRef = useRef(null);

  const openModal = () => {
    if (disabled) return;
    setHoverRows(0);
    setHoverCols(0);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (forceOpen && !disabled) {
      openModal();
      onAfterOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen, disabled]);

  const initCells = (rows, cols) => {
    const next = {};
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) next[`${r}-${c}`] = "";
    }
    return next;
  };

  const applyValueToState = (incoming) => {
    if (!incoming) return;

    const rows = (incoming.tableRow?.length || 0) + 1;
    const cols = incoming.tableColumn?.length || 0;

    if (rows <= 1 || cols <= 0) {
      setGenerated(false);
      setTableName(incoming.tableTitle || "");
      setSelectedRows(0);
      setSelectedCols(0);
      setCellValues({});
      return;
    }

    const base = initCells(rows, cols);

    incoming.tableColumn.forEach((c, i) => {
      base[`0-${i}`] = c?.name ?? "";
    });

    incoming.tableRow.forEach((r, i) => {
      base[`${i + 1}-0`] = r?.name ?? "";
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(base);
    setTableName(incoming.tableTitle || "");
    setGenerated(true);
  };

  useEffect(() => {
    if (!isModalOpen && skipPrefillOnCloseRef.current) {
      skipPrefillOnCloseRef.current = false;
      return;
    }
    if (!isModalOpen) applyValueToState(value);
  }, [value, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      if (!modalPrefilledRef.current) {
        applyValueToState(value);
        modalPrefilledRef.current = true;
      }
    } else {
      modalPrefilledRef.current = false;
    }
  }, [isModalOpen, value]);

  const handleModalUpdate = () => {
    if (disabled) return;

    const rows = selectedRows || hoverRows;
    const cols = selectedCols || hoverCols;
    if (!rows || !cols) return;

    const prev = cellValues || {};
    const merged = initCells(rows, cols);

    Object.keys(prev).forEach((k) => {
      const [r, c] = k.split("-").map(Number);
      if (r < rows && c < cols) merged[k] = prev[k];
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(merged);
    setGenerated(true);
    skipPrefillOnCloseRef.current = true;
    closeModal();
  };

  const handleCellChange = (r, c, v) => {
    if (disabled) return;
    setCellValues((prev) => ({ ...prev, [`${r}-${c}`]: v }));
  };

  const computePayload = () => {
    const tableColumn = [];
    for (let c = 0; c < selectedCols; c++) {
      tableColumn.push({
        name: (cellValues[`0-${c}`] ?? "").trim(),
        lineType: "line item",
      });
    }

    const tableRow = [];
    for (let r = 1; r < selectedRows; r++) {
      tableRow.push({
        name: (cellValues[`${r}-0`] ?? "").trim(),
        lineType: "line item",
      });
    }

    return {
      tableTitle: tableName ?? "",
      tableRow,
      tableColumn,
    };
  };

  useEffect(() => {
    if (disabled || !generated || isModalOpen) return;

    const payload = computePayload();
    const payloadStr = JSON.stringify(payload);

    if (lastSentPayloadRef.current === payloadStr) return;
    lastSentPayloadRef.current = payloadStr;

    onChange?.(payload);
  }, [
    disabled,
    cellValues,
    tableName,
    selectedRows,
    selectedCols,
    generated,
    isModalOpen,
  ]);

  const dragRowIndexRef = useRef(null);

  const moveRow = (from, to) => {
    if (from === to) return;

    setCellValues((prev) => {
      const next = {};
      const rows = selectedRows;
      const cols = selectedCols;

      // map row indices
      const order = [...Array(rows).keys()];
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);

      order.forEach((oldR, newR) => {
        for (let c = 0; c < cols; c++) {
          next[`${newR}-${c}`] = prev[`${oldR}-${c}`] ?? "";
        }
      });

      return next;
    });
  };

  return (
    <div
      style={{
        margin: "8px 0",
        border: "1px solid #e3efe7",
        borderRadius: 12,
        padding: 12,
        marginTop: "-33px",
        backgroundColor: "#fff",
        overflow: "hidden",
        // width:"88vw",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
        }}
      >
        <h5 style={{ margin: 0, color: "#205a46" }}>
          {tableName || "Untitled Table"}
        </h5>

        {/* ICON COMPLETELY HIDDEN WHEN DISABLED */}
        {!disabled && <EditIcon onClick={openModal} />}
      </div>

      {generated && (
        <div
          style={{
            marginTop: 12,
            overflowX: "auto", // ✅ horizontal scroll only
            width: "100%",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              width: "max-content", // 🔑 allows table to exceed container width
              minWidth: "100%",
              border: "1px solid #ccc",
            }}
          >
            <tbody>
              {[...Array(selectedRows)].map((_, r) => (
                <tr
                  key={r}
                  onDragOver={(e) => allowReorder && e.preventDefault()}
                  onDrop={() => {
                    if (!allowReorder || dragRowIndexRef.current == null)
                      return;
                    moveRow(dragRowIndexRef.current, r);
                    dragRowIndexRef.current = null;
                  }}
                >
                  {[...Array(selectedCols)].map((_, c) => {
                    const editable = r === 0 || c === 0;
                    const key = `${r}-${c}`;

                    return (
                      <td
                        key={c}
                        style={{
                          border: "1px solid #ccc",
                          padding: "6px 8px",
                          minWidth: "120px",
                          background: editable ? "#fff" : "#f8f8f8",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {/* 🔹 Drag icon INSIDE first column */}
                          {allowReorder && c === 0 && r > 0 && (
                            <span
                              draggable
                              onDragStart={() => (dragRowIndexRef.current = r)}
                              style={{
                                cursor: "grab",
                                color: "#888",
                                fontSize: 14,
                                userSelect: "none",
                                padding: 0,
                              }}
                              title="Reorder row"
                            >
                              <DragHandleIcon />
                            </span>
                          )}

                          {editable ? (
                            <input
                              type="text"
                              value={cellValues[key] ?? ""}
                              disabled={disabled}
                              title={cellValues[key] ?? ""} // ✅ tooltip with full data
                              onChange={(e) =>
                                handleCellChange(r, c, e.target.value)
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                fontSize: 14,
                                padding: 0,
                                cursor: "default",
                              }}
                            />
                          ) : (
                            // <span style={{ color: "#555", flex: 1 }}>
                            //   {cellValues[key] || ""}
                            // </span>
                            <span
                              className={libcss.truncateTwolines}
                              title={cellValues[key] || ""} 
                              style={{
                                color: "#555",
                                flex: 1,
                                lineHeight: "1.4",
                                cursor: "default",
                              }}
                            >
                              {cellValues[key] || ""}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && !disabled && (
        <div
          aria-modal="true"
          role="dialog"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
        <div
          style={{ width: 600, background: "#fff", borderRadius: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            padding: 30, display: "flex", flexDirection: "column", gap: 20 }}  >
        <div
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #eaeeed",  margin: "0 -30px", padding: "0 30px 20px"}}  >
            <h3
            style={{fontWeight: 500, fontSize: "20px", lineHeight: "100%", letterSpacing: "0px",  margin: 0 }} >
            Configure Table
          </h3>

         <img src={closeIcon} alt="Close" onClick={closeModal}
          style={{ width: 20, height: 20, cursor: "pointer" }}  />
      </div>
        
         <div >
          <label
            style={{ fontSize: 13, fontWeight: 500, color: "#33584e",  marginBottom: 6, display: "block", }} >
            Table Name
          </label>

            <input
              name="tableName"
              value={tableName}
              placeholder="Type Something..."
              onChange={(e) => setTableName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #c2e6d1",
               // marginBottom: 16,
                backgroundColor: "#f4faf5",
              }}
            />
            </div>

            <div >
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                marginBottom: 4,
                display: "block",
              }}
            >
              Table Layout (Rows × Columns)
            </label>

            <div
            style={{
              width: "100%",
              maxwidth: 540,
              height: 347,
              border: "1px solid #eaeeed",
              borderRadius: 16,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              overflow: "hidden", 
            }}
          >


           <div
            style={{
              flex: 1,               
              overflowY: "auto",
              overflowX: "hidden",
              width: "100%",
            }}
          >
            <SizePicker
              totalRows={totalRows}
              totalCols={totalCols}
              hoverRows={hoverRows}
              hoverCols={hoverCols}
              selectedRows={selectedRows}
              selectedCols={selectedCols}
              onHover={(r, c) => {
                setHoverRows(r);
                setHoverCols(c);
              }}
              onPick={(r, c) => {
                setSelectedRows(r);
                setSelectedCols(c);
                setHoverRows(r);
                setHoverCols(c);
              }}
            />
          </div>
             <div style={{textAlign: "center", fontWeight: 500, color: "#333" }}>
              {(selectedRows || hoverRows) > 0 &&
              (selectedCols || hoverCols) > 0
                ? `${selectedRows || hoverRows} Rows × ${
                    selectedCols || hoverCols
                  } Columns`
                : "Select table size"}
            </div>
          </div>
        </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 ,
                alignItems: "center", 
              }}>
              <button 
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
                disabled={
                  // require an explicit selected size OR a preview (hover)
                  !(selectedRows || hoverRows) || !(selectedCols || hoverCols)
                }
                title={
                  !(selectedRows || hoverRows) || !(selectedCols || hoverCols)
                    ? "Pick a size first"
                    : "Create table"
                }
                onClick={handleModalUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SizePicker({
  totalRows,
  totalCols,
  hoverRows,
  hoverCols,
  selectedRows,
  selectedCols,
  onHover,
  onPick,
}) {
  return (
     <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        width: "fit-content",
        maxwidth: "100%", 
       // border: "1px solid #e5e7eb",
      }}
    >

      {[...Array(totalRows)].map((_, r) => (
        <div key={r} style={{ display: "flex"}}>
          {[...Array(totalCols)].map((_, c) => {
            const rr = r + 1;
            const cc = c + 1;
            const active =
              rr <= (selectedRows || hoverRows) &&
              cc <= (selectedCols || hoverCols);
            return (
              <div
                key={c}
                onMouseEnter={() => onHover(rr, cc)}
                onClick={() => onPick(rr, cc)}
                style={{
                  width: 18,
                  height: 18,
                  margin: 2,
                  borderRadius: 3.36,
                  border: active ? "1px solid #3ab67d" : "1px solid #ccc",
                  backgroundColor: active ? "#e4f2e6" : "#eaeeed",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
